import React from 'react';
import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Center,
  Flex,
  HStack,
  Spacer,
  Stack,
  StackSeparator,
  useDisclosure
} from '@chakra-ui/react';
import NextLink from 'next/link';
import { HiOutlineMenu, HiX } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import { DarkModeSwitch } from '@/components/DarkModeSwitch';
import { LanguageSelector } from '@/components/LanguageSelector';
import { useAuthState } from '@/contexts/AuthContext';
import { NavList } from './NavList';
import { NavListItem } from './NavListItem';

export interface LinkType {
  title?: string;
  titleKey?: string;
  url: string;
  props?: ButtonProps;
}

const links: { anonymous: LinkType[]; authenticated: LinkType[] } = {
  anonymous: [
    {
      titleKey: 'nav.blog',
      url: 'https://blog.eezi.io/',
      props: { color: 'accent-alt' }
    },
    {
      titleKey: 'nav.contactUs',
      url: '/#contact-us',
      props: { color: 'accent-alt' }
    },
    {
      titleKey: 'nav.signIn',
      url: '/sign-in/',
      props: {
        variant: 'solid',
        colorScheme: 'brand',
        bgColor: 'brand.500',
        _hover: {
          bgColor: 'brand.600'
        }
      }
    }
  ],
  authenticated: [
    {
      titleKey: 'nav.blog',
      url: 'https://blog.eezi.io/',
      props: { color: 'accent-alt' }
    },
    {
      titleKey: 'nav.contactUs',
      url: '/#contact-us',
      props: { color: 'accent-alt' }
    },
    {
      titleKey: 'nav.signOut',
      url: '/sign-out/',
      props: { color: 'accent-alt' }
    },
    {
      titleKey: 'nav.dashboard',
      url: `${process.env.NEXT_PUBLIC_URL}/dashboard`,
      props: {
        variant: 'solid',
        colorScheme: 'brand',
        bgColor: 'brand.500',
        _hover: {
          bgColor: 'brand.600'
        }
      }
    }
  ]
};

const DesktopNavContent = (props: BoxProps) => {
  const { isAuthenticated } = useAuthState();
  const { t } = useTranslation('common');

  return (
    <Box as='nav' {...props}>
      <Flex justify='space-between'>
        <Spacer />
        <HStack gap={4} mr={4}>
          {/*<Menu.Root>
            <Menu.Trigger asChild>
              <Button variant='ghost' size='md' fontSize='1rem'>
                Solutions <PiCaretDownBold />
              </Button>
            </Menu.Trigger>
            <Menu.Positioner>
              <Menu.Content>
                <Menu.Item value='e-Invoicing and e-Reporting'>
                  <NextLink href='/#e-invoicing'>e-Invoicing and e-Reporting</NextLink>
                </Menu.Item>
                <Menu.Item value='VAT Returns and Reporting'>
                  <NextLink href='/#vat-returns'>VAT Returns and Reporting</NextLink>
                </Menu.Item>
                <Menu.Item value='VAT Recovery' asChild>
                  <NextLink href='/#vat-recovery'>VAT Recovery</NextLink>
                </Menu.Item>
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>*/}

          {/* Authenticated links */}
          {isAuthenticated &&
            links.authenticated.map((link) => (
              <Button asChild key={link.titleKey} variant='ghost' size='md' fontSize='1rem' {...link.props}>
                <NextLink href={link.url}>{t(link.titleKey)}</NextLink>
              </Button>
            ))}

          {/* Non-authenticated links */}
          {!isAuthenticated &&
            links.anonymous.map((link) => (
              <Button asChild key={link.titleKey} variant='ghost' size='md' fontSize='1rem' {...link.props}>
                <NextLink href={link.url}>{t(link.titleKey)}</NextLink>
              </Button>
            ))}
          <LanguageSelector />
          <DarkModeSwitch />
        </HStack>
      </Flex>
    </Box>
  );
};
const MobileNavContent = (props: BoxProps) => {
  const { open, onToggle } = useDisclosure();
  const { t } = useTranslation('common');

  return (
    <Box {...props}>
      <Center as='button' p='2' fontSize='2xl' color='gray.400' _dark={{ color: 'gray.100' }} onClick={onToggle}>
        {open ? <HiX /> : <HiOutlineMenu />}
      </Center>
      <NavList pos='absolute' insetX='0' bg='brand.500' top='64px' animate={open ? 'enter' : 'exit'}>
        <Stack gap={0} separator={<StackSeparator borderColor='whiteAlpha.200' />}>
          {links.anonymous.map((link) => (
            <NavListItem key={link.titleKey} m={1}>
              <Box textAlign='center' fontWeight='bold' py='5' fontSize='lg' _hover={{ bg: 'blackAlpha.200' }}>
                <Button
                  variant='ghost'
                  asChild
                  display='block'
                  textAlign='center'
                  fontWeight='bold'
                  py='5'
                  fontSize='lg'
                  color='white'
                  w='full'
                  _hover={{ bg: 'blackAlpha.200' }}
                >
                  <NextLink href={link.url}>{t(link.titleKey)}</NextLink>
                </Button>
              </Box>
            </NavListItem>
          ))}
        </Stack>
      </NavList>
    </Box>
  );
};
export const NavContent = {
  Mobile: MobileNavContent,
  Desktop: DesktopNavContent
};
